/**
 * Settings ▸ Tiers — the one place an Engagement Standard Tier is configured.
 *
 * Before this screen existed, a tier's rate card lived at the bottom of the Rate Card
 * page, its default Shape and penalty step inside the Shapes page, and its points,
 * minimum Expectation Level and ceiling penalty scaling factor nowhere at all. Nothing
 * listed what a tier actually was.
 *
 * A CARD PER TIER, NOT A TABLE. There are exactly four tiers and each carries a dozen
 * values across three unrelated concerns (commercial, staffing, scoring). A twelve-column
 * table would be unreadable and would still hide the rate-card warning, which is the one
 * thing on this page that can block work.
 *
 * NO CREATE OR DELETE, DELIBERATELY. The four names are pinned by the
 * `engagement_standard_tier_name_valid` CHECK and by string-union types across the app,
 * and four RESTRICT foreign keys mean a tier in use cannot be removed. See
 * src/api/engagement-standard-tiers.ts.
 */

import { AlertTriangle, Pencil } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { type TierConfigView, useTiers } from "@/hooks/use-engagement-standard-tiers";
import { usePodShapes } from "@/hooks/use-pod-shapes";
import { useRateCard } from "@/hooks/use-rate-card";
import { useSession } from "@/lib/auth-client";
import { hasCapability } from "@/lib/permissions";
import { TierEditDialog } from "./TierEditDialog";

/** One labelled value. The label is what makes a bare number mean something. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-medium tabular-nums">{children}</p>
    </div>
  );
}

function TierCard({
  tier,
  canEdit,
  onEdit,
}: {
  tier: TierConfigView;
  canEdit: boolean;
  onEdit: () => void;
}) {
  return (
    <Card>
      {/* CardHeader is a grid that only splits into two columns when it sees a
          CardAction — flex utilities on the header cannot override that, they just
          leave the action stacked under the title. */}
      <CardHeader>
        <CardTitle className="text-base">{tier.name}</CardTitle>
        <p className="max-w-xl text-sm text-muted-foreground">
          {tier.description ?? "No description."}
        </p>
        {canEdit && (
          <CardAction>
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="mr-1 h-3.5 w-3.5" />
              Edit
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Rate card</span>
          <Badge variant={tier.rateBasisComplete ? "secondary" : "outline"}>
            {tier.rateBasisName || "Not mapped"}
          </Badge>
          {!tier.rateBasisComplete && (
            <span className="text-xs font-medium text-warning">No rates</span>
          )}
        </div>

        {tier.rateBasisIncompleteMessage && (
          <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-50 px-4 py-3 text-sm text-warning-950 dark:bg-warning-950/20 dark:text-warning-100">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{tier.rateBasisIncompleteMessage}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <Field label="Default Shape">
            {tier.defaultShapeName
              ? `${tier.defaultShapeName} · ${tier.defaultShapeComposition}`
              : `None · ${tier.defaultPodSize}`}
          </Field>
          <Field label="Pod points (R1–R4)">
            {tier.defaultR1Points} · {tier.defaultR2Points} · {tier.defaultR3Points} ·{" "}
            {tier.defaultR4Points ?? "—"}
          </Field>
          <Field label="Min Expectation Level">{tier.minimumExpectationLevel}</Field>
          <Field label="Expectation penalty step">{tier.expectationPenaltyStep}</Field>
          <Field label="Ceiling penalty factor">{tier.ceilingPenaltyScalingFactor}</Field>
        </div>
      </CardContent>
    </Card>
  );
}

export function TierLibrary() {
  const { data: tiers = [], isLoading, isError, error, refetch } = useTiers();
  const { data: card } = useRateCard();
  const { data: shapes = [] } = usePodShapes();
  const { data: session } = useSession();
  const [editing, setEditing] = useState<TierConfigView | null>(null);

  const role = session?.user?.role;
  const canEditDefaults = hasCapability(role, "manageConfiguration");
  const canEditRateCard = hasCapability(role, "manageRateCard");

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load tiers"} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="font-display text-lg font-semibold">Engagement Standard Tiers</h2>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          What each tier commits to: the rate card its Solutions are priced against, the Pod it
          staffs by default, and the scoring inputs behind its Fit Scores. Tiers themselves are
          fixed — Accounts, Solutions and Plans all reference them by name.
        </p>
      </header>

      {isLoading ? (
        <div className="space-y-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {tiers.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              canEdit={canEditDefaults || canEditRateCard}
              onEdit={() => setEditing(tier)}
            />
          ))}
        </div>
      )}

      {editing && (
        <TierEditDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          // Re-read from the freshly invalidated list so a reopened dialog shows what was
          // just saved rather than the row captured when the button was clicked.
          tier={tiers.find((t) => t.id === editing.id) ?? editing}
          bases={card?.bases ?? []}
          shapes={shapes}
          canEditRateCard={canEditRateCard}
          canEditDefaults={canEditDefaults}
        />
      )}
    </div>
  );
}
